import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../utils/api';
import TagService from '../Tags/TagService';
import {
  Upload,
  Link as LinkIcon,
  File as FileIcon,
  Image as ImageIcon,
  X,
  Download,
  Edit2,
  Trash2,
  ExternalLink,
  Paperclip,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];

function formatBytes(bytes) {
  const numeric = Number(bytes);
  if (!numeric || Number.isNaN(numeric)) return '0 B';
  if (numeric === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.floor(Math.log(numeric) / Math.log(1024));
  const value = numeric / 1024 ** idx;
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isImageFile(filename) {
  if (!filename) return false;
  return IMAGE_EXTENSIONS.some((ext) => filename.toLowerCase().endsWith(ext));
}

function getFileIcon(attachment) {
  if (!attachment) return <FileIcon className="h-5 w-5 text-gray-400" />;
  if (attachment.AttachmentType === 'URL') {
    return <ExternalLink className="h-5 w-5 text-blue-500" />;
  }
  if (isImageFile(attachment.OriginalFileName)) {
    return <ImageIcon className="h-5 w-5 text-green-500" />;
  }
  return <FileIcon className="h-5 w-5 text-gray-500" />;
}

/**
 * AttachmentServiceV2 - Modern attachment management with drag-and-drop and multiple file uploads
 */
const AttachmentServiceV2 = ({ entityType, entityId, onAttachmentChange }) => {
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const { isDark } = useTheme();
  const dropZoneRef = useRef(null);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasPermission, setHasPermission] = useState(true); // Track if user has permission to view attachments

  // Upload queue for multiple files
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(false);

  // URL attachment modal
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlForm, setUrlForm] = useState({
    url: '',
    title: '',
    description: '',
    tags: '',
  });

  // Edit attachment modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    tags: '',
  });

  // Load attachments
  const loadAttachments = async () => {
    if (!entityType || !entityId) {
      setAttachments([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = `/attachments/${entityType}/${entityId}`;
      const data = await api.get(endpoint);
      const attachmentList = data.attachments || data || [];
      setAttachments(attachmentList);
      setHasPermission(true);
    } catch (err) {
      console.error('Failed to load attachments:', err);
      // Handle permission errors gracefully - don't show error for 403
      if (err.message && err.message.includes('Insufficient permissions')) {
        // Silently handle permission errors - user doesn't have access
        setError(null);
        setHasPermission(false);
      } else {
        setError(err.message || 'Failed to load attachments');
        setHasPermission(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttachments();
  }, [entityType, entityId]);

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addFilesToQueue(files);
    }
  };

  // File selection handler
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addFilesToQueue(files);
    }
    e.target.value = ''; // Reset input
  };

  // Add files to upload queue
  const addFilesToQueue = (files) => {
    const newItems = files.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      title: file.name,
      description: '',
      tags: '',
      status: 'pending', // pending, uploading, success, error
      progress: 0,
      error: null,
    }));
    setUploadQueue((prev) => [...prev, ...newItems]);
  };

  // Remove item from queue
  const removeFromQueue = (id) => {
    setUploadQueue((prev) => prev.filter((item) => item.id !== id));
  };

  // Update queue item
  const updateQueueItem = (id, updates) => {
    setUploadQueue((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  };

  // Upload all files in queue
  const uploadAllFiles = async () => {
    const pendingItems = uploadQueue.filter((item) => item.status === 'pending');
    if (pendingItems.length === 0) return;

    setUploading(true);

    for (const item of pendingItems) {
      try {
        updateQueueItem(item.id, { status: 'uploading', progress: 0 });

        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('title', item.title || item.file.name);
        formData.append('description', item.description || '');
        formData.append('tags', item.tags || '');

        const uploadEndpoint = `/attachments/${entityType}/${entityId}/upload`;

        const response = await fetch(`${api.baseURL}/api${uploadEndpoint}`, {
          method: 'POST',
          headers: (() => {
            const headers = api.getAuthHeaders();
            delete headers['Content-Type'];
            return headers;
          })(),
          body: formData,
        });

        if (!response.ok) {
          let errorMessage = 'Failed to upload';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            errorMessage = `${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        updateQueueItem(item.id, { status: 'success', progress: 100 });
      } catch (err) {
        console.error('Upload failed:', err);
        updateQueueItem(item.id, {
          status: 'error',
          error: err.message || 'Upload failed',
        });
      }
    }

    setUploading(false);
    await loadAttachments();
    if (onAttachmentChange) onAttachmentChange();

    // Clear successful uploads after a delay
    setTimeout(() => {
      setUploadQueue((prev) => prev.filter((item) => item.status !== 'success'));
    }, 2000);
  };

  // Delete attachment
  const handleDelete = async (attachment) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const response = await fetch(`${api.baseURL}/api/attachments/${attachment.AttachmentID}`, {
        method: 'DELETE',
        headers: api.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete attachment');
      }

      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message || 'Failed to delete attachment');
    }
  };

  // Download attachment
  const handleDownload = async (attachment) => {
    try {
      const response = await fetch(
        `${api.baseURL}/api/attachments/download/${attachment.AttachmentID}`,
        { headers: api.getAuthHeaders() }
      );

      if (!response.ok) throw new Error('Failed to download');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.OriginalFileName || attachment.Title || 'file';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError(err.message || 'Failed to download');
    }
  };

  // Open edit modal
  const openEditModal = (attachment) => {
    setEditingAttachment(attachment);
    setEditForm({
      title: attachment.Title || '',
      description: attachment.Description || '',
      tags: Array.isArray(attachment.Tags)
        ? attachment.Tags.join(', ')
        : typeof attachment.Tags === 'string'
        ? attachment.Tags
        : '',
    });
    setShowEditModal(true);
  };

  // Handle edit attachment submission
  const handleEditSubmit = async () => {
    if (!editingAttachment) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${api.baseURL}/api/attachments/${editingAttachment.AttachmentID}`, {
        method: 'PUT',
        headers: api.getAuthHeaders(),
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to update attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setShowEditModal(false);
      setEditingAttachment(null);
      setEditForm({ title: '', description: '', tags: '' });
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to update attachment:', err);
      setError(err.message || 'Failed to update attachment');
    } finally {
      setLoading(false);
    }
  };

  // Handle URL attachment submission
  const handleUrlSubmit = async () => {
    if (!urlForm.url) {
      setError('Please provide a valid URL.');
      return;
    }

    try {
      new URL(urlForm.url);
    } catch {
      setError('URL format is invalid. Example: https://example.com/file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/attachments/${entityType}/${entityId}/url`, {
        url: urlForm.url,
        title: urlForm.title,
        description: urlForm.description,
        tags: urlForm.tags,
      });

      if (response.error) {
        throw new Error(response.error);
      }

      setShowUrlModal(false);
      setUrlForm({
        url: '',
        title: '',
        description: '',
        tags: '',
      });
      await loadAttachments();
      if (onAttachmentChange) onAttachmentChange();
    } catch (err) {
      console.error('Failed to add URL attachment:', err);
      setError(err.message || 'Failed to add URL attachment');
    } finally {
      setLoading(false);
    }
  };

  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-gray-300' : 'text-gray-600';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Paperclip className="h-5 w-5 text-gray-400" />
          <h3 className={`text-base font-medium ${textPrimary}`}>
            <span className="text-xs sm:text-sm md:text-base">{safeT('attachments.title', 'Attachments')}</span> ({attachments.length})
          </h3>
        </div>
        {hasPermission && (
          <button
            type="button"
            onClick={() => setShowUrlModal(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <LinkIcon className="h-4 w-4" />
            <span className="text-xs sm:text-sm">{safeT('attachments.addUrl', 'Add URL')}</span>
          </button>
        )}
      </div>

      {/* Drag and Drop Zone - Only show if user has permission */}
      {hasPermission && (
      <div>
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative rounded-lg border-2 border-dashed transition-colors ${
          isDragOver
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
        } p-8`}
      >
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id={`file-upload-${entityType}-${entityId}`}
        />
        <label
          htmlFor={`file-upload-${entityType}-${entityId}`}
          className="flex flex-col items-center justify-center cursor-pointer"
        >
          <Upload className={`h-12 w-12 mb-3 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className={`text-sm font-medium ${textPrimary} mb-1`}>
            {isDragOver
              ? safeT('attachments.dropFiles', 'Drop files here')
              : safeT('attachments.dragOrClick', 'Drag files here or click to browse')}
          </p>
          <p className="text-xs text-gray-500">
            <span className="text-xs sm:text-sm">{safeT('attachments.multipleFiles', 'You can upload multiple files at once')}</span>
          </p>
        </label>
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className={`rounded-lg border ${cardBg} p-4 space-y-3`}>
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-medium ${textPrimary}`}>
              Upload Queue ({uploadQueue.filter((i) => i.status === 'pending').length} pending)
            </h4>
            <button
              type="button"
              onClick={uploadAllFiles}
              disabled={uploading || uploadQueue.every((i) => i.status !== 'pending')}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Upload All'}
            </button>
          </div>

          {uploadQueue.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                isDark ? 'bg-gray-700/50' : 'bg-gray-100'
              }`}
            >
              <div className="flex-shrink-0">
                {item.status === 'success' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : item.status === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <FileIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${textPrimary} truncate`}>{item.file.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(item.file.size)}</p>
                {item.status === 'error' && (
                  <p className="text-xs text-red-500 mt-1">{item.error}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {item.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => removeFromQueue(item.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
      )}
      {/* End of permission-gated upload section */}

      {/* Error Message */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-200">
          {error}
        </div>
      )}

      {/* Attachments List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : attachments.length === 0 ? (
        <div className="text-center py-8">
          <Paperclip className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            <span className="text-xs sm:text-sm">
              {!hasPermission 
                ? safeT('attachments.noPermission', 'You do not have permission to view attachments for this record')
                : safeT('attachments.noAttachments', 'No attachments yet')
              }
            </span>
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div
              key={attachment.AttachmentID}
              className={`rounded-lg border p-4 ${cardBg} hover:shadow-md transition-shadow`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">{getFileIcon(attachment)}</div>
                <div className="flex-1 min-w-0">
                  <h4 className={`text-sm font-semibold ${textPrimary} mb-1`}>
                    {attachment.Title || attachment.OriginalFileName || 'Untitled'}
                  </h4>
                  {attachment.Description && (
                    <p className={`text-sm ${textSecondary} mb-2`}>{attachment.Description}</p>
                  )}
                  {attachment.AttachmentType === 'URL' && attachment.URL && (
                    <a
                      href={attachment.URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700 mb-2"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {attachment.URL}
                    </a>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                    <span>{attachment.UploadedByName || 'Unknown'}</span>
                    <span>•</span>
                    <span>{formatDate(attachment.CreatedAt)}</span>
                    <span>•</span>
                    <span>{formatBytes(attachment.FileSize)}</span>
                  </div>
                  <TagService
                    entityType="Attachment"
                    entityId={attachment.AttachmentID}
                    initialTags={
                      Array.isArray(attachment.Tags)
                        ? attachment.Tags
                        : typeof attachment.Tags === 'string'
                        ? attachment.Tags.split(',').map((t) => t.trim()).filter(Boolean)
                        : []
                    }
                    maxTags={10}
                    onChange={loadAttachments}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditModal(attachment)}
                    className="p-2 text-gray-600 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(attachment)}
                    className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Attachment Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div
            className={`w-full max-w-lg rounded-2xl border ${cardBg} p-6 shadow-xl m-4`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className={`text-lg font-semibold ${textPrimary}`}>
                  <span className="text-xs sm:text-sm md:text-base">{safeT('attachments.editAttachment', 'Edit Attachment')}</span>
                </h2>
                <p className={`text-sm ${textSecondary}`}>
                  {editingAttachment?.OriginalFileName || editingAttachment?.Title || 'Attachment'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAttachment(null);
                  setEditForm({ title: '', description: '', tags: '' });
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.nameTitle', 'Name/Title')}</span>
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder={safeT('attachments.titlePlaceholder', 'Enter a title')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.description', 'Description')}</span>
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder={safeT('attachments.descriptionPlaceholder', 'Manage files, images and links shared across the system')}
                  rows={3}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.tags', 'Tags')}</span>
                </label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  placeholder={safeT('attachments.tagsPlaceholder', 'Comma separated')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAttachment(null);
                  setEditForm({ title: '', description: '', tags: '' });
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                type="button"
                onClick={handleEditSubmit}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-700 disabled:opacity-50"
              >
                <Edit2 className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{loading ? safeT('common.saving', 'Saving...') : safeT('common.saveChanges', 'Save changes')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* URL Attachment Modal */}
      {showUrlModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div
            className={`w-full max-w-lg rounded-2xl border ${cardBg} p-6 shadow-xl m-4`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className={`text-lg font-semibold ${textPrimary}`}>
                  <span className="text-xs sm:text-sm md:text-base">{safeT('attachments.addUrlAttachment', 'Add URL Attachment')}</span>
                </h2>
                <p className={`text-sm ${textSecondary}`}>
                  <span className="text-xs sm:text-sm">{safeT('attachments.addLinkDescription', 'Reference an external resource')}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowUrlModal(false);
                  setUrlForm({ url: '', title: '', description: '', tags: '' });
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">URL *</label>
                <input
                  type="url"
                  value={urlForm.url}
                  onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })}
                  placeholder="https://example.com/document.pdf"
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.title', 'Title')}</span>
                </label>
                <input
                  type="text"
                  value={urlForm.title}
                  onChange={(e) => setUrlForm({ ...urlForm, title: e.target.value })}
                  placeholder={safeT('attachments.titlePlaceholder', 'Enter a title')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.description', 'Description')}</span>
                </label>
                <textarea
                  value={urlForm.description}
                  onChange={(e) => setUrlForm({ ...urlForm, description: e.target.value })}
                  placeholder={safeT('attachments.descriptionPlaceholder', 'Add a description')}
                  rows={3}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="text-sm font-medium">
                  <span className="text-xs sm:text-sm">{safeT('attachments.tags', 'Tags')}</span>
                </label>
                <input
                  type="text"
                  value={urlForm.tags}
                  onChange={(e) => setUrlForm({ ...urlForm, tags: e.target.value })}
                  placeholder={safeT('attachments.tagsPlaceholder', 'Add tags (comma-separated)')}
                  className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
                    isDark
                      ? 'border-gray-600 bg-gray-700 text-white'
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowUrlModal(false);
                  setUrlForm({ url: '', title: '', description: '', tags: '' });
                }}
                className="rounded-lg px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span className="text-xs sm:text-sm">{safeT('common.cancel', 'Cancel')}</span>
              </button>
              <button
                type="button"
                onClick={handleUrlSubmit}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-green-700 disabled:opacity-50"
              >
                <LinkIcon className="h-4 w-4" />
                <span className="text-xs sm:text-sm">{loading ? safeT('common.adding', 'Adding...') : safeT('common.add', 'Add')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

AttachmentServiceV2.propTypes = {
  entityType: PropTypes.string.isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAttachmentChange: PropTypes.func,
};

AttachmentServiceV2.defaultProps = {
  onAttachmentChange: undefined,
};

export default AttachmentServiceV2;

